import { useNavigate } from 'react-router-dom';
import { ShieldIcon } from './ForgeTransition';
import { motion } from 'framer-motion';
import { RotateCcw } from 'lucide-react';

const steps = [
  { label: 'Sign In', route: '/auth' },
  { label: 'Assess', route: '/assess' },
  { label: 'Learn', route: '/learn' },
  { label: 'Stats', route: '/stats/backtest' },
  { label: 'Firm', route: '/firm' },
  { label: 'Protocol', route: '/prescription' },
  { label: 'Dashboard', route: '/dashboard' },
];

interface ProgressTrackerProps {
  currentStep: number; // 1-based index
}

export function ProgressTracker({ currentStep }: ProgressTrackerProps) {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-[720px] mx-auto px-4 pt-8 pb-4">
      <div className="flex items-center justify-between">
        {steps.map(({ label, route }, i) => {
          const stepNum = i + 1;
          const isComplete = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;
          const isClickable = isComplete;

          return (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              {/* Shield node */}
              <div className="flex flex-col items-center">
                <motion.div
                  className={`relative ${isClickable ? 'cursor-pointer' : ''}`}
                  initial={false}
                  animate={{
                    scale: isCurrent ? 1.15 : 1,
                  }}
                  whileHover={isClickable ? { scale: 1.2 } : undefined}
                  whileTap={isClickable ? { scale: 0.95 } : undefined}
                  transition={{ type: 'spring' as const, stiffness: 300, damping: 20 }}
                  onClick={() => isClickable && navigate(route)}
                >
                  <ShieldIcon
                    size={28}
                    filled={isComplete || isCurrent}
                    glowIntensity={isCurrent ? 0.6 : isComplete ? 0.2 : 0}
                  />
                  {/* Step number inside shield */}
                  <span
                    className={`
                      absolute inset-0 flex items-center justify-center text-[9px] font-mono font-semibold
                      ${isCurrent ? 'text-[#A8B4C0]' : isComplete ? 'text-[#A8B4C0]/60' : 'text-white/15'}
                    `}
                    style={{ paddingTop: 2 }}
                  >
                    {isComplete ? '✓' : stepNum}
                  </span>
                </motion.div>
                <span
                  className={`
                    text-[10px] font-mono tracking-wider mt-2 whitespace-nowrap uppercase
                    ${isClickable ? 'cursor-pointer' : ''}
                    ${isCurrent ? 'text-[#A8B4C0]/80' : isComplete ? 'text-white/35' : 'text-white/15'}
                  `}
                  onClick={() => isClickable && navigate(route)}
                >
                  {label}
                </span>
              </div>

              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="flex-1 mx-1.5 mt-[-16px] relative h-px">
                  {/* Base line */}
                  <div className={`
                    absolute inset-0
                    ${isComplete ? 'bg-[#A8B4C0]/15' : 'bg-white/[0.03]'}
                  `} />
                  {/* Fill line for completed */}
                  {isComplete && (
                    <motion.div
                      className="absolute inset-0 bg-[#A8B4C0]/25"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                      style={{ transformOrigin: 'left' }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Start Over */}
      <div className="flex justify-center mt-3">
        <button
          onClick={() => navigate('/assess')}
          className="flex items-center gap-1.5 text-[10px] font-mono text-white/20 hover:text-[#A8B4C0]/60 transition-colors cursor-pointer uppercase tracking-wider"
        >
          <RotateCcw className="w-3 h-3" />
          Start Over
        </button>
      </div>
    </div>
  );
}
